/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import { center as turf_center } from '@turf/turf';
import { dateUtils } from '@opengeoweb/shared';
import type { CoreAppStore } from '../types';
import { selectorMemoizationOptions } from '../utils';
import { SYNCGROUPS_TYPE_SETTIME } from './syncGroups/constants';
import {
  getFeatureLinksForAllPanelsFromState,
  getLinkedFeatureSourcePanelIdsForMapId,
  getLinkedTargetIdsForFeatureSourcePanel,
} from './syncGroups/featureLinks';
import type {
  FeatureLayerData,
  FormFeatureData,
  PotentialData,
  SynchronizationGroupState,
  SynchronizationSource,
} from './syncGroups/types';
import type { SetTimePayload } from './types';
import type { TimeSliderStartEndStepCurrent } from '../map/types';

const emptyPotentialData: PotentialData = {};

const getFeatureSyncStateByPanelId = (
  syncState: SynchronizationGroupState | undefined,
  panelId: string,
): PotentialData | undefined =>
  syncState?.sources?.byId?.[panelId]?.featureSyncState;

const synchronizationGroupStore = (
  store: CoreAppStore,
): SynchronizationGroupState => (store?.syncGroups ? store.syncGroups : null!);

export const getSynchronizationGroupStore = (
  store: CoreAppStore,
): SynchronizationGroupState => {
  return store?.syncGroups ? store.syncGroups : null!;
};

/**
 * Returns the synchronization source by id
 * @param store The app  store
 * @param id The source id to find
 */
const getSyncSourceBySourceId = (
  state: SynchronizationGroupState,
  id: string,
): SynchronizationSource => {
  if (state?.sources) {
    const { sources } = state;
    if (sources.byId[id]) {
      return sources.byId[id];
    }
  }
  return null!;
};

export const getTime = createSelector(
  getSyncSourceBySourceId,
  (store) =>
    store?.payloadByType?.[SYNCGROUPS_TYPE_SETTIME]
      ? (store.payloadByType[SYNCGROUPS_TYPE_SETTIME] as SetTimePayload).value
      : null,
  selectorMemoizationOptions,
);

export const selectFeatureLinksForAllPanels = createSelector(
  synchronizationGroupStore,
  (syncState): Record<string, string[]> =>
    getFeatureLinksForAllPanelsFromState(syncState),
);

export const getLinkedTargetIds = createSelector(
  [
    synchronizationGroupStore,
    (store, panelId?: string): string | undefined => panelId,
  ],
  (syncState, panelId): string[] =>
    getLinkedTargetIdsForFeatureSourcePanel(syncState, panelId),
  selectorMemoizationOptions,
);

export const getLinkedMapIds = createSelector(
  [
    getLinkedTargetIds,
    (store: CoreAppStore): string[] => store?.webmap?.allIds || [],
  ],
  (linkedTargets, mapIds): string[] =>
    linkedTargets.filter((targetId) => mapIds.includes(targetId)),
  selectorMemoizationOptions,
);

export const selectSharedData = createSelector(
  [synchronizationGroupStore, (store, panelId: string): string => panelId],
  (syncState, panelId): PotentialData => {
    const featureSyncStateForPanel = getFeatureSyncStateByPanelId(
      syncState,
      panelId,
    );

    return featureSyncStateForPanel || emptyPotentialData;
  },
  selectorMemoizationOptions,
);

export const selectLinkedPanelIdsForMap = createSelector(
  [selectFeatureLinksForAllPanels, (_, mapViewId: string): string => mapViewId],
  (linksByPanelId, mapViewId): string[] =>
    getLinkedFeatureSourcePanelIdsForMapId(linksByPanelId, mapViewId),
);

export const getSelectedFeature = createSelector(
  [
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    (state: SynchronizationGroupState, mapId: string) => {
      return getFeatureSyncStateByPanelId(state, mapId);
    },
  ],
  (sharedData) => {
    if (!sharedData) {
      return null;
    }
    const { selectedFeatureId } = sharedData;
    const featureLayers = Object.values(sharedData.featureLayersById ?? {});
    if (!featureLayers.length || !selectedFeatureId) {
      return null;
    }
    const selectedFeature = featureLayers
      .flatMap((layer) => layer.geoJSON.features)
      .find((feature) => feature.id === selectedFeatureId);

    if (selectedFeature?.geometry.type === 'Point') {
      const coordinates = selectedFeature.geometry.coordinates as [
        number,
        number,
      ];
      return {
        lat: coordinates[1],
        lon: coordinates[0],
        id: selectedFeature.id as string,
        name: selectedFeature.properties?.name,
        occurrences: selectedFeature.properties?.occurrences,
        serviceId: selectedFeature.properties?.serviceId,
      };
    }

    if (selectedFeature) {
      const center = turf_center(selectedFeature);

      return {
        lat: center.geometry.coordinates[1],
        lon: center.geometry.coordinates[0],
        id: selectedFeature.id as string,
        name: selectedFeature.properties?.name,
        occurrences: selectedFeature.properties?.occurrences,
      };
    }

    return null;
  },
);

export const getSelectedFeatureAsGeoJSON = createSelector(
  [
    (
      state: SynchronizationGroupState,
      mapId: string,
    ): PotentialData | undefined => {
      return getFeatureSyncStateByPanelId(state, mapId);
    },
  ],
  (sharedData) => {
    if (!sharedData) {
      return null;
    }
    const { selectedFeatureId } = sharedData;
    const featureLayers = Object.values(sharedData.featureLayersById ?? {});
    if (!featureLayers.length || !selectedFeatureId) {
      return null;
    }
    const selectedFeature = featureLayers
      .flatMap((layer) => layer.geoJSON.features)
      .find((feature) => feature.id === selectedFeatureId);
    return selectedFeature;
  },
);

export const selectLinkedFeatureLayersForMap = createSelector(
  [
    selectFeatureLinksForAllPanels,
    synchronizationGroupStore,
    (_, mapId: string): string => mapId,
  ],
  (linksByPanelId, syncState, mapId): FeatureLayerData[] => {
    const linkedPanels = getLinkedFeatureSourcePanelIdsForMapId(
      linksByPanelId,
      mapId,
    );

    return linkedPanels.flatMap((panelId) =>
      Object.values(
        getFeatureSyncStateByPanelId(syncState, panelId)?.featureLayersById ??
          {},
      ),
    );
  },
  selectorMemoizationOptions,
);

export const selectLinkedSharedDataForMap = createSelector(
  [
    selectFeatureLinksForAllPanels,
    synchronizationGroupStore,
    (_, mapId: string): string => mapId,
  ],
  (linksByPanelId, syncState, mapId): Record<string, PotentialData> => {
    const linkedPanels = getLinkedFeatureSourcePanelIdsForMapId(
      linksByPanelId,
      mapId,
    );

    const linkedSharedData: Record<string, PotentialData> = {};

    linkedPanels.forEach((panelId) => {
      const sharedData = getFeatureSyncStateByPanelId(syncState, panelId);
      if (sharedData) {
        linkedSharedData[panelId] = sharedData;
      }
    });

    return linkedSharedData;
  },
  selectorMemoizationOptions,
);

export const selectLinkedFormFeaturesForMap = createSelector(
  [
    selectFeatureLinksForAllPanels,
    synchronizationGroupStore,
    (_, mapId: string): string => mapId,
  ],
  (linksByPanelId, syncState, mapId): FormFeatureData[] => {
    const linkedPanels = getLinkedFeatureSourcePanelIdsForMapId(
      linksByPanelId,
      mapId,
    );

    return linkedPanels.flatMap(
      (panelId) =>
        getFeatureSyncStateByPanelId(syncState, panelId)?.formFeatures ?? [],
    );
  },
  selectorMemoizationOptions,
);

export const getSyncedStartCenterEndStepCurrentTime = createSelector(
  (coreAppStore, sourceId) =>
    getTime(getSynchronizationGroupStore(coreAppStore), sourceId),
  (store): TimeSliderStartEndStepCurrent | undefined => {
    if (!store?.timeSliderCenterTime || !store?.timeSliderSpan) {
      return undefined;
    }

    return {
      start: Math.round(store.timeSliderCenterTime - store.timeSliderSpan / 2),
      end: Math.round(store.timeSliderCenterTime + store.timeSliderSpan / 2),
      step: store?.timeSliderStep || 60,
      current: dateUtils.unixFromISOString(store?.currentTime),
    } as TimeSliderStartEndStepCurrent;
  },
  selectorMemoizationOptions,
);
